import React from 'react';

import { Text } from '../../../../../../components';
import { PaneCard, Chip, Divider } from '../../../../components/wizard';
import { useConfigurator } from '../../../../store/configurator';
import { ThemeCard } from '../ThemeCard';
import {
  Header,
  Heading,
  CountryRow,
  CountryLeft,
  CountryLabel,
  CountryName,
  Flag,
  ChangeCountry,
  List,
  BrowseRow,
} from './ThemesSection.styles';

export type ThemesSectionProps = {
  /** Open the full-screen themes catalog overlay. */
  onBrowse: () => void;
};

/**
 * "Popular in <country>" theme row. The list keeps a stable order (the
 * catalog-anchored theme + the next five), but the SELECTED card is the big
 * "featured" one (orange wash + brand border + 84px logo) while the rest render
 * compact — selecting another card grows it and shrinks the previous, in place.
 */
export const ThemesSection = ({ onBrowse }: ThemesSectionProps): React.ReactElement | null => {
  const { themes, selection, featuredTheme, setTheme, country, openCountryPicker } = useConfigurator();

  if (!featuredTheme) return null;

  // Featured hero + up to five more = six themes on the pane (the rest live in the catalog).
  const others = themes.filter((t) => t.id !== featuredTheme.id).slice(0, 5);

  return (
    <PaneCard>
      <Header>
        <Heading text="Themes" tag="paragraph" />
        <CountryRow>
          <CountryLeft>
            <CountryLabel text="Popular in" tag="span" />
            <CountryName>
              {country?.flag ? <Flag src={country.flag} alt="" /> : null}
              {country?.name ? <Text text={country.name} tag="span" /> : null}
            </CountryName>
          </CountryLeft>
          <ChangeCountry type="button" data-ga-id="cfg-design-country-change" onClick={openCountryPicker}>
            <Text text="Change country" tag="span" />
          </ChangeCountry>
        </CountryRow>
      </Header>

      <Divider />

      <List>
        <ThemeCard
          theme={featuredTheme}
          featured={featuredTheme.id === selection.themeId}
          selected={featuredTheme.id === selection.themeId}
          onSelect={() => setTheme(featuredTheme.id)}
        />
        {others.map((theme) => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            featured={theme.id === selection.themeId}
            selected={theme.id === selection.themeId}
            onSelect={() => setTheme(theme.id)}
          />
        ))}
      </List>

      <BrowseRow>
        <Chip onClick={onBrowse} gaId="cfg-design-themes-browse">
          <Text text="Browse {{count}} themes" placeholders={{ count: themes.length }} tag="span" />
        </Chip>
      </BrowseRow>
    </PaneCard>
  );
};

export default ThemesSection;
